import type { Placement } from '@popperjs/core';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { usePopper } from 'react-popper';
import { useTheme } from 'styled-components';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';

export interface UseDropdownPopperProps {
  isOpen: boolean;
  dropdownWidth?: CSSProperties['width'];
  dropdownHeight?: CSSProperties['height'];
  dropdownPlacement?: Placement;
  /** The referenceElement is the element which the dropdown is anchored to */
  referenceElement: HTMLElement | null;
  onClickOutside?: (e: MouseEvent) => void;
  offset?: number;
  padding?: number;
  /** The dropdownContentRef will be attached to the container of the content within the dropdown */
  dropdownContentRef?: RefObject<HTMLDivElement>;
  /**
   * If set to false, will not react to size or position changes in the reference element.
   * The dropdown will just update as isOpen becomes true. Defaults to true.
   */
  reactToRefElementChanges?: boolean;
  /** An option on Popper.js's preventOverflow modifier. Defaults to true.
   *
   * When disabled, "disconnects" / untethers the popper from the anchor element. This tells Popper.js that it is okay
   * to position the popper in a "disconnected" manner from the anchor element if that is necessary to satisfy the
   * "preventOverflow" mandate.
   *
   * Makes sense to use when you have a large popper, and are at risk of having smaller area to render within.
   */
  tetherToRefElement?: boolean;
  /** If true, we will not wait for an Animation Frame before updating the position of the dropdown when the content size
   * within the dropdown changes. This can help with fluidity in some cases. Defaults to false (waiting for an animation frame).
   */
  immediateContentSizeUpdate?: boolean;
}

export interface UseDropdownPopperOutput {
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  popper: ReturnType<typeof usePopper>;
  dropdownWidth?: CSSProperties['width'];
  dropdownHeight?: CSSProperties['height'];
  referenceElement: HTMLElement | null;
  isOpen: boolean;
  dropdownContentRef: RefObject<HTMLDivElement>;
}

export const useDropdownPopper = ({
  isOpen,
  dropdownPlacement,
  dropdownWidth,
  dropdownHeight,
  referenceElement,
  onClickOutside,
  offset = 0,
  padding = 0,
  dropdownContentRef,
  reactToRefElementChanges = true,
  immediateContentSizeUpdate = false,
  tetherToRefElement = true,
}: UseDropdownPopperProps): UseDropdownPopperOutput => {
  const { spacing4 } = useTheme();
  const _offset = offset || spacing4;
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  // if we are applying a manual width, we want the dropdown to be just bottom. This will split the additional width evenly across left and right sides.
  // if we are going for auto-width, then set it to bottom-start to align dropdown with input field exactly.
  const popperPlacement = dropdownPlacement ? dropdownPlacement : dropdownWidth ? 'bottom' : 'bottom-start';
  const popper = usePopper(referenceElement, popperElement, {
    placement: popperPlacement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, _offset],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding,
          tether: tetherToRefElement,
          altAxis: !tetherToRefElement, // Allows the popover to be shifted along the altAxis, possibly overlapping target element
        },
      },
      {
        name: 'eventListeners',
        options: {
          scroll: reactToRefElementChanges,
          resize: reactToRefElementChanges,
        },
      },
      {
        name: 'computeStyles',
        options: {
          adaptive: reactToRefElementChanges,
        },
      },
    ],
  });
  const { update } = popper;

  const defaultDropdownContentRef = useRef<HTMLDivElement>(null);
  const dropdownContentRefToUse = dropdownContentRef ?? defaultDropdownContentRef;

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      onClickOutside && onClickOutside(e);
    },
    [onClickOutside]
  );

  useOnClickOutside(dropdownContentRefToUse, isOpen ? handleClickOutside : undefined);

  // We need to manually listen to resizes in the referenceElement
  // and when that resizes, we update the popper to follow along.
  useEffect(() => {
    if (referenceElement && update && reactToRefElementChanges) {
      const observer = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          update();
        });
      });
      observer.observe(referenceElement);
      return () => observer.disconnect();
    }
  }, [referenceElement, reactToRefElementChanges, update]);

  // Similarly to above, we also listen to size changed in the content of our dropdown.
  // When the height changes, we might have to update our position. Usually this is just done on init+1 after
  // the component realizes what height its content takes up.
  useEffect(() => {
    if (dropdownContentRefToUse.current && update) {
      const observer = new ResizeObserver(() => {
        if (immediateContentSizeUpdate) {
          update();
        } else {
          window.requestAnimationFrame(() => {
            update();
          });
        }
      });
      observer.observe(dropdownContentRefToUse.current);
      return () => observer.disconnect();
    }
  }, [dropdownContentRefToUse, immediateContentSizeUpdate, update]);

  // We also need to call update every time the referenceElement changes
  // https://github.com/floating-ui/floating-ui/issues/538#issuecomment-596601887
  useEffect(() => {
    update && update();
  }, [referenceElement, update]);

  // Whenever we open the dropdown, lets update the position as well to make sure its opened in the right place
  useEffect(() => {
    if (isOpen && update) {
      update();
    }
  }, [isOpen, update]);

  return {
    setPopperElement,
    popper,
    dropdownWidth,
    dropdownHeight,
    referenceElement,
    isOpen,
    dropdownContentRef: dropdownContentRefToUse,
  };
};
